import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import { resolveBaseAppearance } from './theme';
import type { BaseInputGroupProps } from './types';
import { cn, hasRenderableNode } from './utils';

export const BaseInputGroup = forwardRef<
  HTMLDivElement,
  BaseInputGroupProps
>(function BaseInputGroup(
  {
    children,
    startElement,
    endElement,
    startAddon,
    endAddon,
    invalid = false,
    disabled = false,
    appearance,
    className,
    classNames,
    style,
    ...groupProps
  },
  ref,
) {
  for (const [name, value] of [
    ['invalid', invalid],
    ['disabled', disabled],
  ] as const) {
    if (typeof value !== 'boolean') {
      throw new TypeError(`BaseInputGroup ${name} must be a boolean.`);
    }
  }

  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <div
      {...componentAttributes}
      {...groupProps}
      ref={ref}
      data-monkeys-component="input-group"
      data-slot="root"
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      className={cn(
        theme.slots.inputGroupRoot,
        invalid && theme.slots.inputGroupInvalid,
        disabled && theme.slots.inputGroupDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.inputGroup,
        ...(invalid ? theme.styles.inputGroupInvalid : undefined),
        ...(disabled ? theme.styles.inputGroupDisabled : undefined),
        ...style,
      }}
    >
      {hasRenderableNode(startAddon) ? (
        <span
          data-slot="start-addon"
          data-placement="start"
          className={cn(
            theme.slots.inputGroupStartAddon,
            classNames?.startAddon,
          )}
          style={theme.styles.inputGroupAddon}
        >
          {startAddon}
        </span>
      ) : null}
      {hasRenderableNode(startElement) ? (
        <span
          data-slot="start-element"
          data-placement="start"
          className={cn(
            theme.slots.inputGroupStartElement,
            classNames?.startElement,
          )}
          style={theme.styles.inputGroupElement}
        >
          {startElement}
        </span>
      ) : null}
      <div
        data-slot="content"
        className={cn(
          theme.slots.inputGroupContent,
          classNames?.content,
        )}
        style={theme.styles.inputGroupContent}
      >
        {children}
      </div>
      {hasRenderableNode(endElement) ? (
        <span
          data-slot="end-element"
          data-placement="end"
          className={cn(
            theme.slots.inputGroupEndElement,
            classNames?.endElement,
          )}
          style={theme.styles.inputGroupElement}
        >
          {endElement}
        </span>
      ) : null}
      {hasRenderableNode(endAddon) ? (
        <span
          data-slot="end-addon"
          data-placement="end"
          className={cn(
            theme.slots.inputGroupEndAddon,
            classNames?.endAddon,
          )}
          style={theme.styles.inputGroupAddon}
        >
          {endAddon}
        </span>
      ) : null}
    </div>
  );
});
